import {
    useEffect,
    useState,
} from "react";

import { useAuth }
    from "../context/AuthContext";

import { useNavigate }
    from "react-router-dom";

import {
    getUserOrders
} from "../services/bookingService";

import {
    calculateTotalHours,
} from "../utils/orderUtils";

import OrderHistoryHeader
    from "../components/orderHistory/OrderHistoryHeader";

import OrderHistoryStats
    from "../components/orderHistory/OrderHistoryStats";

import OrderHistoryCard
    from "../components/orderHistory/OrderHistoryCard";

function OrderHistoryPage() {

    const {
        currentUser,
        isAuthenticated,
    } = useAuth();

    const navigate =
        useNavigate();

    const [orders, setOrders]
        = useState([]);

    const [loading, setLoading]
        = useState(true);

    const [loadingMore, setLoadingMore]
        = useState(false);

    const [filter, setFilter]
        = useState("all");

    const [dateFilter, setDateFilter]
        = useState("all");

    const [lastDoc, setLastDoc]
        = useState(null);

    const [hasMore, setHasMore]
        = useState(false);

    /* ---------------------------------- */
    /* LOAD INITIAL ORDERS */
    /* ---------------------------------- */

    useEffect(() => {

        async function loadOrders() {

            if (!currentUser) {

                setOrders([]);

                setLoading(false);

                return;
            }

            try {

                setLoading(true);

                const response =
                    await getUserOrders({

                        userId:
                            currentUser.uid,

                        status:
                            filter,

                        dateFilter,

                        lastDoc: null,
                    });

                setOrders(
                    response.orders || []
                );

                setLastDoc(
                    response.lastDoc || null
                );

                setHasMore(
                    response.hasMore || false
                );

            } catch (error) {

                console.error(

                    "Failed loading orders:",

                    error
                );

                setOrders([]);

            } finally {

                setLoading(false);
            }
        }

        loadOrders();

    }, [

        currentUser,

        filter,

        dateFilter,
    ]);

    /* ---------------------------------- */
    /* LOAD MORE */
    /* ---------------------------------- */

    async function loadMoreOrders() {

        if (
            !currentUser ||
            !lastDoc ||
            loadingMore
        ) {

            return;
        }

        try {

            setLoadingMore(true);

            const response =
                await getUserOrders({

                    userId:
                        currentUser.uid,

                    status:
                        filter,

                    dateFilter,

                    lastDoc,
                });

            setOrders((prev) => [

                ...prev,

                ...(response.orders || []),
            ]);

            setLastDoc(
                response.lastDoc || null
            );

            setHasMore(
                response.hasMore || false
            );

        } catch (error) {

            console.error(

                "Failed loading more orders:",

                error
            );

        } finally {

            setLoadingMore(false);
        }
    }

    /* ---------------------------------- */
    /* TOTAL HOURS */
    /* ---------------------------------- */

    const totalHours =
        calculateTotalHours(
            orders
        );

    /* ---------------------------------- */
    /* AUTH */
    /* ---------------------------------- */

    if (!isAuthenticated) {

        return (

            <div style={styles.authPage}>

                <div style={styles.authCard}>

                    <h2 style={styles.authTitle}>
                        Sign in to view order history
                    </h2>

                    <button
                        onClick={() =>
                            navigate("/signin")
                        }
                        style={styles.authButton}
                    >
                        Sign In
                    </button>

                </div>

            </div>
        );
    }

    /* ---------------------------------- */
    /* LOADING */
    /* ---------------------------------- */

    if (loading) {

        return (

            <div style={styles.page}>

                <div style={styles.loading}>
                    Loading order history...
                </div>

            </div>
        );
    }

    return (

        <div style={styles.page}>

            <div style={styles.container}>

                {/* HEADER */}

                <OrderHistoryHeader />

                {/* STATS */}

                <OrderHistoryStats

                    totalBookings={
                        orders.length
                    }

                    totalHours={
                        totalHours
                    }
                />

                {/* FILTER SECTION */}

                <div style={styles.filterContainer}>

                    <div style={styles.filterTitle}>
                        Filters
                    </div>

                    <div style={styles.filterRow}>

                        {/* STATUS FILTERS */}

                        <div style={styles.filters}>

                            {[
                                "all",
                                "confirmed",
                                "cancelled",
                            ].map((status) => (

                                <button

                                    key={status}

                                    onClick={() =>
                                        setFilter(status)
                                    }

                                    style={{
                                        ...styles.filterButton,

                                        background:
                                            filter === status
                                                ? "linear-gradient(90deg, #00ff88, #00d4ff)"
                                                : "rgba(255,255,255,0.06)",

                                        color:
                                            filter === status
                                                ? "#041018"
                                                : "#ffffff",

                                        border:
                                            filter === status
                                                ? "1px solid rgba(0,255,136,0.45)"
                                                : "1px solid rgba(255,255,255,0.12)",

                                        boxShadow:
                                            filter === status
                                                ? "0 0 20px rgba(0,255,136,0.18)"
                                                : "none",
                                    }}
                                >

                                    {status.charAt(0).toUpperCase() +
                                        status.slice(1)}

                                </button>
                            ))}

                        </div>

                        {/* DATE FILTER */}

                        <div style={styles.dropdownWrapper}>

                            <select

                                value={dateFilter}

                                onChange={(e) =>
                                    setDateFilter(
                                        e.target.value
                                    )
                                }

                                style={styles.dateDropdown}
                            >

                                <option value="all">
                                    All Time
                                </option>

                                <option value="week">
                                    Last Week
                                </option>

                                <option value="month">
                                    Last Month
                                </option>

                                <option value="3months">
                                    Last 3 Months
                                </option>

                            </select>

                        </div>

                    </div>

                </div>

                {/* EMPTY */}

                {orders.length === 0 ? (

                    <div style={styles.emptyState}>

                        <h3 style={styles.emptyTitle}>
                            No bookings found
                        </h3>

                        <p style={styles.emptyText}>
                            Try changing filters or create a booking.
                        </p>

                    </div>

                ) : (

                    <>
                        <div style={styles.list}>

                            {orders.map(
                                (
                                    booking,
                                    index
                                ) => (

                                    <OrderHistoryCard

                                        key={
                                            `${booking.bookingId}_${index}`
                                        }

                                        booking={
                                            booking
                                        }
                                    />
                                )
                            )}

                        </div>

                        {/* LOAD MORE */}

                        {hasMore && (

                            <div style={styles.loadMoreWrapper}>

                                <button

                                    onClick={
                                        loadMoreOrders
                                    }

                                    disabled={
                                        loadingMore
                                    }

                                    style={styles.loadMoreButton}
                                >

                                    {loadingMore
                                        ? "Loading..."
                                        : "Load More"}

                                </button>

                            </div>
                        )}
                    </>
                )}

            </div>

        </div>
    );
}

const styles = {

    page: {

        minHeight: "100vh",

        padding: "40px 20px",

        background:
            "linear-gradient(111.1deg, rgb(0,40,70) -4.8%, rgb(255,229,115) 82.7%, rgb(255,203,15) 97.2%)",
    },

    container: {

        maxWidth: "1200px",

        margin: "0 auto",
    },

    filterContainer: {

        background:
            "rgba(0,40,70,0.68)",

        border:
            "1px solid rgba(255,255,255,0.08)",

        borderRadius: "24px",

        padding: "24px",

        marginBottom: "30px",

        backdropFilter: "blur(14px)",
    },

    filterTitle: {

        color: "#ffffff",

        fontSize: "18px",

        fontWeight: "700",

        marginBottom: "18px",
    },

    filterRow: {

        display: "flex",

        justifyContent: "space-between",

        alignItems: "center",

        gap: "18px",

        flexWrap: "wrap",
    },

    filters: {

        display: "flex",

        gap: "12px",

        flexWrap: "wrap",
    },

    filterButton: {

        padding: "10px 18px",

        borderRadius: "999px",

        cursor: "pointer",

        fontWeight: "700",

        fontSize: "14px",

        transition: "all 0.2s ease",

        backdropFilter: "blur(10px)",
    },

    dropdownWrapper: {

        position: "relative",
    },

    dateDropdown: {

        padding: "12px 16px",

        borderRadius: "14px",

        border:
            "1px solid rgba(255,255,255,0.14)",

        background:
            "rgba(255,255,255,0.08)",

        color: "#ffffff",

        fontSize: "14px",

        fontWeight: "600",

        outline: "none",

        cursor: "pointer",

        backdropFilter: "blur(10px)",

        minWidth: "190px",
    },

    list: {

        display: "flex",

        flexDirection: "column",

        gap: "20px",
    },

    loadMoreWrapper: {

        display: "flex",

        justifyContent: "center",

        marginTop: "32px",
    },

    loadMoreButton: {

        padding: "14px 28px",

        borderRadius: "999px",

        border: "none",

        cursor: "pointer",

        fontWeight: "700",

        fontSize: "15px",

        background:
            "linear-gradient(90deg, #00ff88, #00d4ff)",

        color: "#041018",

        minWidth: "180px",
    },

    loading: {

        color: "#ffffff",

        fontSize: "18px",

        textAlign: "center",

        marginTop: "120px",
    },

    emptyState: {

        marginTop: "40px",

        textAlign: "center",

        background:
            "rgba(0,40,70,0.72)",

        border:
            "1px solid rgba(255,255,255,0.1)",

        borderRadius: "24px",

        padding: "60px 30px",

        backdropFilter: "blur(12px)",
    },

    emptyTitle: {

        color: "#ffffff",

        fontSize: "28px",

        fontWeight: "800",

        marginBottom: "12px",
    },

    emptyText: {

        color: "#cbd5e1",

        fontSize: "16px",
    },

    authPage: {

        minHeight: "100vh",

        display: "flex",

        justifyContent: "center",

        alignItems: "center",

        background:
            "linear-gradient(111.1deg, rgb(0,40,70) -4.8%, rgb(255,229,115) 82.7%, rgb(255,203,15) 97.2%)",
    },

    authCard: {

        background:
            "rgba(0,40,70,0.85)",

        padding: "40px",

        borderRadius: "24px",

        textAlign: "center",

        border:
            "1px solid rgba(0,255,136,0.2)",

        backdropFilter: "blur(12px)",
    },

    authTitle: {

        color: "#ffffff",

        marginBottom: "24px",

        fontSize: "28px",

        fontWeight: "800",
    },

    authButton: {

        padding: "14px 26px",

        borderRadius: "999px",

        border: "none",

        cursor: "pointer",

        fontWeight: "700",

        fontSize: "15px",

        background:
            "linear-gradient(90deg, #00ff88, #00d4ff)",

        color: "#041018",
    },
};

export default OrderHistoryPage;